import { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { he } from "./he";
import { startDomTranslation } from "./translateDom";
import type { Language } from "./types";

const STORAGE_KEY = "woozyai.language";

type LanguageContextValue = {
  lang: Language;
  dir: "ltr" | "rtl";
  isRtl: boolean;
  /** Translate an English source string. Falls back to the string itself. */
  t: (source: string) => string;
  /** Prefix a site path with the current language segment. */
  lp: (path: string) => string;
  /** Build the equivalent path in the other language. */
  altPath: (target: Language) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export const stripLangPrefix = (pathname: string) => {
  if (pathname === "/he") return "/";
  if (pathname.startsWith("/he/")) return pathname.slice(3) || "/";
  return pathname || "/";
};

export const langFromPathname = (pathname: string): Language =>
  pathname === "/he" || pathname.startsWith("/he/") ? "he" : "en";

export const localizePath = (path: string, lang: Language) => {
  const base = path.startsWith("/") ? path : `/${path}`;
  if (lang === "en") return base;
  return base === "/" ? "/he" : `/he${base}`;
};

export const LanguageProvider = ({ lang, children }: { lang: Language; children: ReactNode }) => {
  const { pathname } = useLocation();
  const dir = lang === "he" ? "rtl" : "ltr";

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", dir);
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* storage unavailable - language still works for this session */
    }
  }, [lang, dir]);

  // Translate inline page copy once Hebrew is active. Language switches are
  // full page navigations, so no reverse pass is needed.
  useEffect(() => {
    if (lang !== "he") return;
    return startDomTranslation();
  }, [lang]);

  const t = useCallback(
    (source: string) => {
      if (lang === "en") return source;
      const value = he[source];
      return value && value.length > 0 ? value : source;
    },
    [lang],
  );

  const lp = useCallback((path: string) => localizePath(path, lang), [lang]);

  const altPath = useCallback(
    (target: Language) => localizePath(stripLangPrefix(pathname), target),
    [pathname],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ lang, dir, isRtl: lang === "he", t, lp, altPath }),
    [lang, dir, t, lp, altPath],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const context = useContext(LanguageContext);
  if (context) return context;
  // Safe default for components rendered outside a provider (e.g. tests).
  return {
    lang: "en",
    dir: "ltr",
    isRtl: false,
    t: (source: string) => source,
    lp: (path: string) => path,
    altPath: () => "/",
  };
};

export const useT = () => useLanguage().t;

export const getStoredLanguage = (): Language | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "he" || stored === "en" ? stored : null;
  } catch {
    return null;
  }
};
